import { Component, OnInit, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { CurrentContextService } from '../../core/context/current-context.service';
import { LocalizationService } from '../../core/localization/localization.service';
import { AinPageComponent } from '../../shared/design-system/page.component';

type CalendarSystemOption = 'gregorian' | 'jalali';

@Component({
  standalone: true,
  selector: 'ain-platform-localization-preferences',
  imports: [AinPageComponent, FormsModule, ButtonModule, InputTextModule, SelectModule],
  template: `
    <ain-page [title]="t('localizationPreferences.title')" [description]="t('localizationPreferences.description')">
      <section class="form-grid">
        <label>
          <span>{{ t('localizationPreferences.calendarSystem') }}</span>
          <p-select
            [options]="calendarOptions"
            optionLabel="label"
            optionValue="id"
            [(ngModel)]="model.calendarSystem"
            appendTo="body" />
        </label>

        <label>
          <span>{{ t('localizationPreferences.culture') }}</span>
          <p-select
            [options]="cultureOptions"
            optionLabel="label"
            optionValue="id"
            [(ngModel)]="model.culture"
            [filter]="true"
            filterBy="label,id"
            appendTo="body" />
        </label>

        <label>
          <span>{{ t('localizationPreferences.timeZone') }}</span>
          <p-select
            [options]="timeZoneOptions"
            optionLabel="label"
            optionValue="id"
            [(ngModel)]="model.timeZone"
            [filter]="true"
            filterBy="label,id"
            appendTo="body" />
        </label>
      </section>

      <div class="actions">
        <button pButton type="button" [label]="t('common.save')" (click)="save()" [disabled]="saving()"></button>
      </div>

      @if (message()) {
        <p class="message">{{ message() }}</p>
      }
    </ain-page>
  `,
  styles: [`
    .form-grid { display: grid; gap: 12px; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); }
    label { display: grid; gap: 6px; }
    label span { font-size: 12px; color: #475569; font-weight: 600; }
    .actions { margin-top: 12px; display: flex; justify-content: flex-end; }
    .message { margin-top: 10px; color: #166534; }
  `]
})
export class PlatformLocalizationPreferencesComponent implements OnInit {
  private readonly contextService = inject(CurrentContextService);
  private readonly localization = inject(LocalizationService);

  readonly saving = signal(false);
  readonly message = signal('');

  readonly calendarOptions = [
    { id: 'jalali', label: this.t('localizationPreferences.calendar.jalali') },
    { id: 'gregorian', label: this.t('localizationPreferences.calendar.gregorian') }
  ];
  readonly cultureOptions = [
    { id: 'fa-IR', label: 'fa-IR (Persian)' },
    { id: 'en-US', label: 'en-US (English)' }
  ];
  readonly timeZoneOptions = [
    { id: 'Asia/Tehran', label: 'Asia/Tehran' },
    { id: 'UTC', label: 'UTC' },
    { id: 'Europe/Istanbul', label: 'Europe/Istanbul' },
    { id: 'Europe/Berlin', label: 'Europe/Berlin' },
    { id: 'America/New_York', label: 'America/New_York' }
  ];

  model: { culture: string; timeZone: string; calendarSystem: CalendarSystemOption } = {
    culture: 'fa-IR',
    timeZone: 'Asia/Tehran',
    calendarSystem: 'jalali'
  };

  ngOnInit(): void {
    const context = this.contextService.context();
    if (!context) {
      this.contextService.loadContext().subscribe({
        next: loaded => this.applyContext(loaded)
      });
      return;
    }

    this.applyContext(context);
  }

  private applyContext(context: { culture: string; timeZone: string; calendarSystem: CalendarSystemOption }): void {
    this.model = {
      culture: context.culture,
      timeZone: context.timeZone,
      calendarSystem: context.calendarSystem
    };
  }

  save(): void {
    this.saving.set(true);
    this.message.set('');
    this.contextService.updatePreferences({
      culture: this.model.culture,
      timeZone: this.model.timeZone,
      calendarSystem: this.model.calendarSystem
    }).subscribe({
      next: updatedContext => {
        this.localization.switchCulture(updatedContext.culture);
        this.message.set(this.t('localizationPreferences.saved'));
        this.saving.set(false);
      },
      error: () => {
        this.message.set(this.t('common.saveFailed'));
        this.saving.set(false);
      }
    });
  }

  t(key: string): string {
    return this.localization.translate(key);
  }
}
